import {
  afterNextRender,
  AfterRenderPhase, AfterViewInit,
  Component, ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {SynapiItem} from "../../../models/Synapi/SynapiItem";
import {SynapiService} from "../../../services/synapi.service";
import {SynapiPredicate} from "../../../models/Synapi/SynapiPredicate";
import {Feature, Map, View} from "ol";
import {Cluster, OSM, XYZ} from "ol/source";
import VectorLayer from "ol/layer/Vector";
import TileLayer from "ol/layer/Tile";
import {fromLonLat, transform} from "ol/proj";
import {LineString, Point} from "ol/geom";
import {Fill, Icon, Stroke, Style} from "ol/style";
import VectorSource from "ol/source/Vector";
import {boundingExtent} from "ol/extent";
import VectorImageLayer from "ol/layer/VectorImage";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Polyline} from "ol/format";
import {CommonModule} from "@angular/common";
import {MediaAttachmentComponent} from "../media-attachment/media-attachment.component";
import {RouterLink} from "@angular/router";
import {SynapiBookmarkToggleComponent} from "../synapi-bookmark-toggle/synapi-bookmark-toggle.component";
import CircleStyle from "ol/style/Circle";

@Component({
  selector: 'synapi-map',
  templateUrl: './synapi-map.component.html',
  standalone: true,
  imports: [CommonModule, MediaAttachmentComponent, RouterLink, SynapiBookmarkToggleComponent],
  styleUrls: ['./synapi-map.component.scss']
})

export class SynapiMapComponent implements OnInit,OnChanges,AfterViewInit {
    @Input() item: SynapiItem;
    itemSelected?: SynapiItem;
    markerSelected?: Feature<Point>;
    @Input() isItineraire: boolean;
    public options : any;
    public location = 'No Data';
    @Input() items: SynapiItem[] = [];
    @Input() predicate: SynapiPredicate;
    public viewItemIds: any[] = [];
    public map: Map;
    public markers: Feature<Point>[] = [];
    @Input() waypoints: number[][] = [];
    @Input() profiles: ("driving-car"|"cycling-regular"|"foot-walking")[] = ['driving-car'];
    @Input() defaultProfile :"driving-car"|"cycling-regular"|"foot-walking" = 'driving-car';
    @Input() geolocation = false;
    public clustersLayer: VectorImageLayer<any>;
    public geolocationLayer: VectorImageLayer<any>;
    public color: string;
    public color2: string;
    public color3: string;
    public startTime: number;
    @ViewChild("map") mapRef: ElementRef;
    @ViewChild("mapPopup") mapPopupRef: ElementRef;
    @Output() onPredicateChange = new EventEmitter<SynapiPredicate>();
    @Output() onTotalChange = new EventEmitter<number>();
    @Output() onDistanceChange = new EventEmitter<number>();
    @Output() onDurationChange = new EventEmitter<number>();
    constructor(public synapi: SynapiService, public http: HttpClient){
      this.startTime = Date.now();
    }
    ngOnInit() {
      if(this.item != null){
        this.items = [this.item];
      }
    }

    ngOnChanges(changes: SimpleChanges) {
      if (changes["items"]){
        if (changes["items"].previousValue != changes["items"].currentValue && this.map) {
          this.setAllMarkers();
        }
      }
      if (changes["item"]){
        if (changes["item"].previousValue != changes["item"].currentValue && this.map) {
          if(this.item != null){
            this.items = [this.item];
          }
          this.setAllMarkers();
        }
      }
      if (changes["geolocation"]){
        if (changes["geolocation"].previousValue != changes["geolocation"].currentValue && this.map) {
          if(this.geolocation){
            this.onGeolocation();
          } else {
            if(this.geolocationLayer){
              this.geolocationLayer.setOpacity(0);
            }
            this.setAllMarkers();
          }
        }
      }
    }

    ngAfterViewInit(){
      if(typeof document !== "undefined") {
        this.color = getComputedStyle(document.body).getPropertyValue('--text-brand-dark');
        this.color2 = getComputedStyle(document.body).getPropertyValue('--text-brand-light');
        this.color3 = getComputedStyle(document.body).getPropertyValue('--text-brand');
        this.initMap();
      }
    }
    initMap(){
      this.map = new Map({
        target: this.mapRef.nativeElement,
        layers: [
          new TileLayer({
            source: new OSM()
          })
        ],
        view: new View({
          center: [43.61455, 6.5542578],
          zoom: 15,
          maxZoom:21
        }),
      });
      this.addOSRMProfilesButtons();
      this.onMapReady();
      this.setOnMapMoveEvent();
      this.setOnMapPointerMoveEvent();
      this.setOnMapPointerClickEvent();
      this.setOnMapPostrenderEvent();
      this.setOnMapViewChangeResolution();
      this.calculateRoute(this.defaultProfile,this.waypoints);
    }

  onMapReady() {
    this.setAllMarkers();
    setTimeout(() => {
      this.map.updateSize();
    }, 750);
  }

  onGeolocation() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          const coordinates = transform([lon, lat], 'EPSG:4326', 'EPSG:3857');
          // Create a new Feature with a Point geometry at the geolocation coordinates
          const geolocationFeature = new Feature(new Point(coordinates));
          geolocationFeature.set('name', 'Geolocation');

          // Create a new VectorSource and add the geolocation feature to it
          const geolocationSource = new VectorSource({
            features: [geolocationFeature]
          });

          // Create a new VectorLayer with the geolocation source
          this.geolocationLayer = new VectorImageLayer({
            source: geolocationSource,
            style: (feature, resolution) => {
              return new Style({
                image: new CircleStyle({
                  radius: 1,
                  fill: new Fill({
                    color: this.color3
                  }),
                  stroke: new Stroke({
                    color: this.color2,
                    width: 2
                  })
                })
              });
            }
          });
          this.geolocationLayer.setOpacity(1);
          // Add the geolocation layer to the map
          this.map.addLayer(this.geolocationLayer);

          // For example, you can center the map on the user's location
          this.map.getView().animate({ center: coordinates, zoom: 12 });
        }
      );
    }
  }

  public setOnMapMoveEvent() {
      this.map.on('moveend',()=>{
        if (!this.item) {
          setTimeout(() => {
            this.viewItemIds = [];
            if (this.markers.length > 0) {
              this.markers.forEach(marker => {
                const geometry = marker.getGeometry();
                if(geometry){
                  const coords = geometry.getCoordinates();
                  const lat = coords[0];
                  const lon = coords[1];
                  const mapView = this.map.getView().calculateExtent(this.map.getSize());
                  const latMin = mapView[0];
                  const lonMin = mapView[1];
                  const latMax = mapView[2];
                  const lonMax = mapView[3];
                  if (lat > latMin && lon > lonMin && lat < latMax && lon < lonMax) {
                    const externalId = marker.get('externalId');
                    if (externalId !== undefined && this.viewItemIds.indexOf(parseInt(externalId, 10)) === -1) {
                      this.viewItemIds.push(parseInt(externalId, 10));
                    }
                  }
                }
              });
            }
            this.onTotalChange.emit(this.viewItemIds?.length);
            this.predicate.elements = this.viewItemIds.slice(0, 200);
            this.onPredicateChange.emit(this.predicate);
          }, 300);
      }
    });
  }

  public setAllMarkers() {
    this.markers = [];
    if (this.clustersLayer) {
      this.map.removeLayer(this.clustersLayer);
    }
    for (const item of this.items) {
      if (item.Localisation !== null && item.Localisation.Lat !== 0 && item.Localisation.Lon !== 0) {
        const coordinates = [item.Localisation.Lon, item.Localisation.Lat];
        const iconFeature = new Feature(new Point(transform(coordinates, 'EPSG:4326', 'EPSG:3857')));
        iconFeature.set('item', item);
        iconFeature.set('isWaypoint', this.waypoints.find(waypoint => waypoint[0] === item.Localisation.Lat && waypoint[1] === item.Localisation.Lon));
        if(iconFeature.get('isWaypoint')){
          iconFeature.set('waypointIndex', this.waypoints.findIndex(waypoint => waypoint[0] === item.Localisation.Lat && waypoint[1] === item.Localisation.Lon)+1);
        }
        iconFeature.set('externalId', item.ExternalId); // Assuming ExternalId is the property you want to associate with the feature
        this.markers.push(iconFeature);
      }
    }
    const source = new VectorSource({
      features: this.markers
    });
    this.setClustersLayer(source);
  }

  public setClustersLayer(source:VectorSource<any>) {
    const color = this.color;
    const color2 = this.color2;
    const me = this;
    const clusterSource = new Cluster({
      distance: !me.isItineraire ? 120:0,
      minDistance: !me.isItineraire ? 60:0,
      source: source,
    });
    this.clustersLayer = new VectorImageLayer({
      source: clusterSource,
      zIndex:3,
      style: function (feature) {
        const size = feature.get('features').length;
        const markers = feature.get('features');
        let icon = "";
        let style;
        if (size > 1 && !me.isItineraire) {
            icon = 'data:image/svg+xml;utf8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="24" fill="${color}" stroke="#fff" stroke-width="2"/>
            <text x="50%" y="53%" text-rendering="optimizeLegibility" dominant-baseline="middle" text-anchor="middle" fill="#fff" font-weight="700" font-size="17">${size}</text>
          </svg>
        `);
        } else {
          if (!me.isItineraire || markers[0].get('isWaypoint')) {
            if(markers[0].get('isWaypoint')){
              icon = 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M30 3.75C24.5318 3.7562 19.2894 5.93118 15.4228 9.79777C11.5562 13.6644 9.3812 18.9068 9.375 24.375C9.375 42.0234 28.125 55.3523 28.9242 55.9102C29.2395 56.131 29.6151 56.2495 30 56.2495C30.3849 56.2495 30.7605 56.131 31.0758 55.9102C31.875 55.3523 50.625 42.0234 50.625 24.375C50.6188 18.9068 48.4438 13.6644 44.5772 9.79777C40.7106 5.93118 35.4682 3.7562 30 3.75ZM30 16.875C31.4834 16.875 32.9334 17.3149 34.1668 18.139C35.4001 18.9631 36.3614 20.1344 36.9291 21.5049C37.4968 22.8753 37.6453 24.3833 37.3559 25.8382C37.0665 27.293 36.3522 28.6294 35.3033 29.6783C34.2544 30.7272 32.918 31.4415 31.4632 31.7309C30.0083 32.0203 28.5003 31.8718 27.1299 31.3041C25.7594 30.7364 24.5881 29.7751 23.764 28.5418C22.9399 27.3084 22.5 25.8584 22.5 24.375C22.5 22.3859 23.2902 20.4782 24.6967 19.0717C26.1032 17.6652 28.0109 16.875 30 16.875Z" fill="${color}"/>
                      <circle cx="30" cy="25" r="15" fill="white"/>
                      <text x="30" y="30" text-anchor="middle" fill="#333" font-weight="700">${markers[0].get('waypointIndex')}</text>
                    </svg>`);
              }
            else{
              icon = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none"><path d="M16 2C13.0836 2.00331 10.2877 3.1633 8.22548 5.22548C6.1633 7.28766 5.00331 10.0836 5 13C5 22.4125 15 29.5212 15.4263 29.8188C15.5944 29.9365 15.7947 29.9997 16 29.9997C16.2053 29.9997 16.4056 29.9365 16.5737 29.8188C17 29.5212 27 22.4125 27 13C26.9967 10.0836 25.8367 7.28766 23.7745 5.22548C21.7123 3.1633 18.9164 2.00331 16 2ZM16 9C16.7911 9 17.5645 9.2346 18.2223 9.67412C18.8801 10.1136 19.3928 10.7384 19.6955 11.4693C19.9983 12.2002 20.0775 13.0044 19.9231 13.7804C19.7688 14.5563 19.3878 15.269 18.8284 15.8284C18.269 16.3878 17.5563 16.7688 16.7804 16.9231C16.0044 17.0775 15.2002 16.9983 14.4693 16.6955C13.7384 16.3928 13.1136 15.8801 12.6741 15.2223C12.2346 14.5645 12 13.7911 12 13C12 11.9391 12.4214 10.9217 13.1716 10.1716C13.9217 9.42143 14.9391 9 16 9Z" fill="' + color.replace('#', '%23') + '"/></svg>';
            }
          } else {
            icon = 'data:image/svg+xml;utf8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 300 300">
              <g transform="translate(150,150) scale(1,1)"><ellipse rx="118.712941" ry="118.712941" transform="translate(0,0)" fill="${color}" opacity="0.7" stroke-width="0"/></g><ellipse rx="50" ry="50" transform="translate(150 150)" fill="${color2}" stroke-width="0"/>
          </svg>
          `);
          }
        }
        if (icon) {
          style = new Style({
            image: new Icon({
              src: icon,
              anchor: markers[0].get('isWaypoint')?[0.5, 1] : [0.5, 0.5],
              anchorXUnits: 'fraction',
              anchorYUnits: 'fraction',
              scale: 1 // Adjust scale as needed
            })
          });
        }
        return style;
      },
    });
    this.map.addLayer(this.clustersLayer);
    if(source.getFeatures().length && this.markers.length>0){
      this.map.getView().fit(source.getExtent(), { padding: [20, 20, 20, 20], maxZoom: 15 });
    } else{
      this.map.setView(
        new View({
          center: fromLonLat([6.55425,43.614]),
          zoom: 8
        })
      );
    }
    this.map.updateSize();
    this.setMapClickEvent();
  }

  public setMapClickEvent(){
    if(!this.isItineraire) {
      this.map.on('click', (e) => {
        this.clustersLayer.getFeatures(e.pixel).then((clickedFeatures) => {
          if (clickedFeatures.length) {
            const features = clickedFeatures[0].get('features');
            if (features.length > 1) {
              const extent = boundingExtent(
                features.map((r: any) => r.getGeometry().getCoordinates())
              );
              this.map.getView().fit(extent, {duration: 500, padding: [50, 50, 50, 50]});
            }
          }
        });
      });
    }
  }

  public calculateRoute(profile: string, waypoints: number[][]) {
    if (this.isItineraire) {
      const apikey = '5b3ce3597851110001cf62487eb46ea77d3f4e66bc6f400e92a85f76';
      const osrmUrl = `https://api.openrouteservice.org/v2/directions/${profile}/json`;
      const rwaypoints = waypoints.map((waypoint, index) => { return [waypoint[1], waypoint[0]]; });
      this.http.post(osrmUrl,{coordinates:rwaypoints,roundabout_exits:true},{headers:new HttpHeaders().set('Authorization',  apikey)}).subscribe(response => {
        // @ts-ignore
        const currentRoute = response['routes'][0];
        const polyline = currentRoute['geometry'];
        const distance = currentRoute['summary']['distance'];
        const duration = currentRoute['summary']['duration'];
        this.onDistanceChange.emit(distance);
        this.onDurationChange.emit(duration);
        const geometry = new Polyline({
          factor: 1e5
        }).readGeometry(polyline, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        });

        const routeFeature = new Feature({
          type: 'route',
          geometry: geometry
        });

        const routeLayer = new VectorLayer({
          source: new VectorSource({
            features: [routeFeature],
          }),
          style: new Style({
            stroke: new Stroke({
              color: this.color3,
              width: 5
            })
          })
        });
        routeLayer.set('name', 'route');
        this.map.addLayer(routeLayer);
      });
    }
  }

  ngOnDestroy(){
    if(this.map){
      this.map.setTarget(undefined);
    }
  }

  private addOSRMProfilesButtons() {
      if(this.waypoints.length>0){
        const osrmProfiles = [
          {name: 'driving-car', title: '<i class="lvi-car"></i>'},
          {name: 'cycling-regular', title: '<i class="lvi-bike"></i>'},
          {name: 'foot-walking', title: '<i class="lvi-walk"></i>'}
        ].filter((osrmp) => this.profiles.find((p)=> p == osrmp.name));
        const osrmProfilesDiv = document.createElement('div');
        osrmProfilesDiv.className = 'osrm-profiles';
        if(osrmProfiles.length > 0) {
          osrmProfiles.forEach(p => {
            const button = document.createElement('button');
            button.innerHTML = p.title;
            button.setAttribute("data-name",p.name);
            button.classList.add('osrm-profile-button','button');
            if(p.name == this.defaultProfile){
              button.classList.add('active');
            }
            button.addEventListener('click', (e) => {
              this.removeLayer('route');
              e.stopPropagation();
              const button = e.currentTarget as HTMLButtonElement;
              const buttons = document.querySelectorAll('.osrm-profile-button');
              buttons.forEach(b => {
                b.classList.remove('active');
              });
              button.classList.add('active');
              const name = button.getAttribute("data-name") ?? "driving-car";
              this.calculateRoute(name, this.waypoints);
            });
            osrmProfilesDiv.appendChild(button);
          });
        }
        this.mapRef.nativeElement.appendChild(osrmProfilesDiv);
      }
  }

  public removeLayer(name: string) {
    this.map.getLayers().getArray().slice().forEach(layer => {
      if (layer && layer.get('name') === name) {
        this.map.removeLayer(layer);
      }
    });
  }

  private setOnMapPointerMoveEvent() {
    this.map.on('pointermove', (e) => {
      const pixel = this.map.getEventPixel(e.originalEvent);
      const hit = this.map.hasFeatureAtPixel(pixel);
      this.map.getTargetElement().style.cursor = hit ? 'pointer' : '';
    });
  }

  private setOnMapPointerClickEvent() {
    const me = this;
    this.map.on('click', async (e) => {
      this.itemSelected = undefined;
      this.markerSelected = undefined;
      const pixel = this.map.getEventPixel(e.originalEvent);
      const hit = this.map.forEachFeatureAtPixel(pixel, function (feature, layer) {
        const markers = feature.get('features');
        if (markers?.length >= 1) {
          const marker = markers[0];
          const waypointIndex = marker.get('waypointIndex');
          if(waypointIndex){
            const waypointElement = document.getElementById('Waypoint'+(waypointIndex-1));
            if(waypointElement){
              const waypointElementClient = waypointElement.getBoundingClientRect();
              const waypointTop = + document.documentElement.scrollTop + waypointElementClient.top
              window.scrollTo({top: waypointTop-160, behavior: 'smooth'});
            }
            return;
          }
          if(markers?.length == 1){
            if (marker.get('item')) {
              me.markerSelected = markers[0];
              if(me.markerSelected){
                const markerGeometry = me.markerSelected.getGeometry();
                if(markerGeometry){
                  const markerCoordinates = markerGeometry.getCoordinates();
                  me.map.getView().animate({ center: markerCoordinates, duration: 1000 });
                }
              }
              return feature;
            }
          }
        }
        return null;
      });
      if (hit) {
        const markers = hit.get('features');
        if (markers?.length == 1) {
          const item = markers[0].get('item');
          if (item) {
            const predicate = new SynapiPredicate();
            predicate.id = item.ExternalId;
            this.itemSelected = await this.synapi.getItem(predicate);
            setTimeout(() => {
              this.setPopupPosition();
            },100);
          }
        }
      }
    });
  }

  private setPopupPosition() {
    if(this.markerSelected && this.itemSelected && this.mapPopupRef){
      let featureGeometry = this.markerSelected.getGeometry();
      if(featureGeometry){
        let featureCoordinates = featureGeometry.getCoordinates();
        let pixelPosition = this.map.getPixelFromCoordinate(featureCoordinates);
        this.mapPopupRef.nativeElement.style.left = (pixelPosition[0]-(this.mapPopupRef.nativeElement.clientWidth/2)-10)+"px";
        this.mapPopupRef.nativeElement.style.top = (pixelPosition[1]-this.mapPopupRef.nativeElement.clientHeight-25)+"px";
      }
    }
  }

  private setOnMapPostrenderEvent() {
    this.map.on('postrender',()=>{
      this.setPopupPosition();
    });
  }

  private setOnMapViewChangeResolution(){
    // Separate features on zoom
    const me = this;
    if(this.isItineraire){
      this.map.getView().on('change:resolution', function() {
        const zoom = me.map.getView().getZoom();
        const thresholdZoom = 18; // Set your desired zoom level to separate features

        if (zoom && zoom > thresholdZoom) {
          const clusteredFeatures = me.clustersLayer.getSource().getFeatures();
          clusteredFeatures.forEach((clusteredFeature :any) => {
            const originalFeatures = clusteredFeature.get('features');
            if (originalFeatures.length > 1) {
              // Separate features
              let angle = 0;
              originalFeatures.forEach((feature: any) => {
                if(!feature.get('isWaypoint')){
                  const geometry = feature.getGeometry();
                  if (geometry instanceof Point) {
                    angle += (2 * Math.PI) / originalFeatures.length;
                    const coordinates = geometry.getCoordinates();
                    const offsetX = 30 * (1 + Math.cos(angle)); // Adjust offset as needed
                    const offsetY = 30 * (1 + Math.sin(angle)); // Adjust offset as needed
                    feature.setGeometry(new Point([coordinates[0] + offsetX, coordinates[1] + offsetY]));
                  }
                }
              });
            }
          });
        }
      });
    }
  }
}
